import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '@/components/base/Button';
import { SITE_CONFIG } from '@/config';

const NAV_ITEMS = [
  {
    label: '機能',
    children: [
      { label: '”おもいむすび”とは', path: '/features/' },
      { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
      { label: 'AIガントチャート', path: '/features/ai-gantt/' },
      { label: 'AI日報', path: '/features/ai-daily-report/' },
      { label: 'AIファイル管理', path: '/features/ai-file-management/' },
      { label: 'チーム機能', path: '/features/team-collaboration/' },
      { label: '多言語対応', path: '/features/multilingual/' },
    ],
  },
  {
    label: '料金・導入',
    children: [
      { label: '料金', path: '/pricing/' },
      { label: '無料トライアル', path: '/free-trial/' },
      { label: 'セキュリティ', path: '/security/' },
      { label: 'お問い合わせ', path: '/contact/' },
    ],
  },
  {
    label: 'お役立ち情報',
    children: [
      { label: 'プロジェクト管理入門', path: '/guides/project-management/' },
      { label: 'AI活用', path: '/guides/project-management-ai/' },
      { label: 'WBS', path: '/guides/wbs/' },
      { label: 'ガントチャート', path: '/guides/gantt-chart/' },
      { label: 'Excelテンプレート', path: '/templates/project-management-excel/' },
      { label: 'ツール比較', path: '/compare/project-management-tools/' },
    ],
  },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const location = useLocation();
  // UIUX: ドロップダウンが即閉じない仕組み（①隙間を埋める＋②250msの閉じ猶予）
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDropdown = (idx: number) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(idx);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 250);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm border-b border-background-200/70">
      <nav className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16" aria-label="メインナビゲーション">
        <Link to="/" className="flex items-center shrink-0">
          <img src="/brand/logo-small.png" alt="OMOIMUSUBI ”おもいむすび”" className="h-12 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item, idx) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => openDropdown(idx)}
              onMouseLeave={scheduleClose}
            >
              <button
                onClick={() => (activeDropdown === idx ? setActiveDropdown(null) : openDropdown(idx))}
                className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-md hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                {item.label}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              {activeDropdown === idx && (
                // pt-2の透明ブリッジで隙間を埋める（マウスが途切れない）
                <div className="absolute top-full left-0 z-50 pt-2">
                  <div className="bg-background-50 border border-background-200/70 rounded-lg py-2 min-w-[200px] shadow-lg">
                  {item.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      className={`block px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                        location.pathname === child.path
                          ? 'text-primary-600 bg-primary-50 font-medium'
                          : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <a href={SITE_CONFIG.appUrl} className="text-sm text-foreground-600 hover:text-foreground-950 whitespace-nowrap transition-colors" rel="nofollow" data-cta-name="login" data-cta-location="header">
            ログイン
          </a>
          <Button to="/free-trial/" size="sm" dataCtaName="free_trial" dataCtaLocation="header">
            無料で今すぐお試し
          </Button>
        </div>

        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-foreground-950 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="メニューを開く"
        >
          <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {NAV_ITEMS.map((item) => (
              <div key={item.label} className="py-1">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-2 py-1">{item.label}</p>
                {item.children.map((child) => (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                      location.pathname === child.path
                        ? 'text-primary-600 bg-primary-50 font-medium'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            ))}
            <div className="border-t border-background-200/70 pt-3 flex flex-col gap-2">
              <a href={SITE_CONFIG.appUrl} className="text-sm text-foreground-600 px-3 py-2" rel="nofollow" data-cta-name="login" data-cta-location="header">
                ログイン
              </a>
              <Button to="/free-trial/" size="md" className="w-full justify-center" dataCtaName="free_trial" dataCtaLocation="header">
                無料で今すぐお試し
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
